import { useRouter } from 'next/router'
import { useCallback } from 'react'
import { useQuery } from 'react-query'
import GetProductsFiltered from '@/services/api/products/GetProductsFiltered'
import useUserPreferences from '@/hooks/useUserPreferences'

export interface ProductQueryParams {
  product_type: string
  name?: string
  page: number
  categories: string[]
  options: string[]
  gender: string
  query: string
  sort: string
  count: number
  minPrice: string
  maxPrice: string
  country?: string
  currency?: string
  is_ai?: string
}

export default function useProductQuery() {
  const router = useRouter()
  const { query, pathname } = router
  const preferences = useUserPreferences()

  // -----------------------------
  // Normalize query params
  // -----------------------------
  const params: ProductQueryParams = {
    product_type:
      typeof query.product_type === 'string' ? query.product_type : '1',

    name: typeof query.name === 'string' ? query.name : undefined,

    page: typeof query.page === 'string' ? parseInt(query.page, 10) || 1 : 1,

    categories:
      typeof query.categories === 'string' && query.categories.length > 0
        ? query.categories.split(',')
        : [],

    options:
      typeof query.options === 'string' && query.options.length > 0
        ? query.options.split(',')
        : [],

    gender: typeof query.gender === 'string' ? query.gender : '',
    query: typeof query.query === 'string' ? query.query : '',
    sort: typeof query.sort === 'string' ? query.sort : '',

    count:
      typeof query.count === 'string'
        ? parseInt(query.count, 10) || 0
        : 0,

    minPrice: typeof query.minPrice === 'string' ? query.minPrice : '',
    maxPrice: typeof query.maxPrice === 'string' ? query.maxPrice : '',

    country: preferences?.countryCode || 'IL',
    currency: preferences?.currencyCode || 'ILS',

    // 🔥 IMPORTANT: neutral default
    is_ai: typeof query.is_ai === 'string' ? query.is_ai : '',
  }

  // -----------------------------
  // Generic query updater
  // -----------------------------
  const setParams = useCallback(
    (updates: Partial<Record<string, any>>) => {
      const newQuery: Record<string, any> = {
        ...query,
        ...updates,
      }

      if (updates.categories) {
        newQuery.categories = (updates.categories as string[]).join(',')
      }

      if (updates.options) {
        newQuery.options = (updates.options as string[]).join(',')
      }

      // Remove empty params from URL
      Object.keys(newQuery).forEach(key => {
        if (newQuery[key] === '' || newQuery[key] === undefined) {
          delete newQuery[key]
        }
      })

      router.push(
        { pathname, query: newQuery },
        undefined,
        { shallow: true, scroll: false }
      )
    },
    [query, pathname, router]
  )

  // -----------------------------
  // 🔥 SINGLE SOURCE OF TRUTH
  // -----------------------------
  const setProductMode = useCallback(
    (product_type: string, is_ai: string = '') => {
      setParams({
        product_type,
        is_ai,
        categories: [],
        options: [],
        page: 1,
      })
    },
    [setParams]
  )

  // -----------------------------
  // Other setters
  // -----------------------------
  const setPage = useCallback((page: number) => setParams({ page }), [setParams])
  const setSort = useCallback((sort: string) => setParams({ sort }), [setParams])
  const setCount = useCallback((count: number) => setParams({ count }), [setParams])
  const setSearch = useCallback((search: string) => setParams({ query: search, page: 1 }), [setParams])
  const setMinPrice = useCallback((minPrice: string) => setParams({ minPrice, page: 1 }), [setParams])
  const setMaxPrice = useCallback((maxPrice: string) => setParams({ maxPrice, page: 1 }), [setParams])

  const toggleCategory = useCallback(
    (cat: string) => {
      const cats = params.categories.includes(cat)
        ? params.categories.filter(c => c !== cat)
        : [cat]
      setParams({ categories: cats, page: 1 })
    },
    [params.categories, setParams]
  )

  const toggleOption = useCallback(
    (opt: string) => {
      const opts = params.options.includes(opt)
        ? params.options.filter(o => o !== opt)
        : [...params.options, opt]
      setParams({ options: opts, page: 1 })
    },
    [params.options, setParams]
  )

  // -----------------------------
  // React Query
  // -----------------------------
  const { data, isLoading, isRefetching } = useQuery(
    [
      'products',
      {
        product_type: params.product_type,
        options: params.options.join(','),
        query: params.query,
        categories: params.categories.join(','),
        sort: params.sort,
        page: params.page.toString(),
        count: params.count.toString(),
        minPrice: params.minPrice,
        maxPrice: params.maxPrice,
        country: params.country,
        currency: params.currency,
        is_ai: params.is_ai,
      },
    ],
    GetProductsFiltered,
    {
      staleTime: 300_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )

  return {
    params,
    data,
    isLoading: isLoading || isRefetching,
    isRefetching,

    // setters
    setPage,
    setSort,
    setCount,
    setSearch,
    setMinPrice,
    setMaxPrice,
    toggleCategory,
    toggleOption,

    // 🔥 exported helper
    setProductMode,
  }
}
